import React from "react";
import styled, { css } from "styled-components";
import { Flex } from "../../Flex";
import { Text } from "../../Text";
import { Icon } from "../../Icon";
const PageLink = styled(Text).attrs({ as: "a" }) `
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme: { sizes } }) => sizes[4]}px;
  height: ${({ theme: { sizes } }) => sizes[4]}px;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  ${({ isActive, disabled, theme: { colors: { tableColors }, }, }) => {
    if (isActive || disabled) {
        const backgroundColor = isActive ? tableColors.paginationActiveBackground : "none";
        return css `
        cursor: not-allowed;
        background-color: ${backgroundColor};
        color: ${isActive ? tableColors.paginationActiveText : tableColors.paginationText};

        svg path {
          fill: ${isActive ? tableColors.paginationActiveText : tableColors.paginationText};
        }

        &:hover {
          color: ${isActive ? tableColors.paginationActiveText : tableColors.paginationText};
          background-color: ${backgroundColor};

          svg path {
            fill: ${isActive ? tableColors.paginationActiveText : tableColors.paginationText};
          }
        }
      `;
    }
    return css `
      cursor: pointer;

      &:hover {
        color: ${tableColors.paginationHoverText};
        background-color: ${tableColors.paginationHoverBackground};
      }
    `;
}}
`;
// The maximum number of explicit page links to render in the pagination controls.
const MAX_PAGE_NUMBER_RANGE = 10;
export const calculatePageNumberRange = (pageIndex, pageCount) => {
    const middlePageIndex = Math.floor(MAX_PAGE_NUMBER_RANGE / 2);
    const lastPageIndex = pageCount - 1;
    // empty table case
    if (lastPageIndex === -1) {
        return { endIndex: 0, startIndex: 0 };
    }
    if (pageIndex <= middlePageIndex) {
        return { endIndex: Math.min(MAX_PAGE_NUMBER_RANGE - 1, lastPageIndex), startIndex: 0 };
    }
    if (pageIndex >= lastPageIndex - middlePageIndex + 1) {
        return { endIndex: lastPageIndex, startIndex: Math.max(0, lastPageIndex - MAX_PAGE_NUMBER_RANGE + 1) };
    }
    return {
        endIndex: pageIndex + (MAX_PAGE_NUMBER_RANGE - middlePageIndex - 1),
        startIndex: pageIndex - middlePageIndex,
    };
};
export const Pagination = ({ "data-testid": dataTestId = "paginationControls", table, }) => {
    const { pageIndex } = table.getState().pagination;
    const pageCount = table.getPageCount();
    const { startIndex, endIndex } = calculatePageNumberRange(pageIndex, pageCount);
    const range = new Array(1 + endIndex - startIndex).fill(startIndex).map((v, i) => startIndex + i);
    const pageNumbers = range.map((pageNumberIndex) => (React.createElement(PageLink, { key: `gotoPage-${pageNumberIndex}`, "data-testid": `gotoPage-${pageNumberIndex}`, onClick: () => table.setPageIndex(pageNumberIndex), isActive: pageNumberIndex === pageIndex, p: 2 }, pageNumberIndex + 1)));
    return (React.createElement(Flex, { "data-testid": dataTestId, justifyContent: "center", alignItems: "center", m: 3 },
        React.createElement(PageLink, { "data-testid": "firstPage", key: "firstPage", p: 2, disabled: !table.getCanPreviousPage(), onClick: () => table.firstPage() },
            React.createElement(Icon, { src: "ChevronDoubleLeft" })),
        React.createElement(PageLink, { "data-testid": "previousPage", key: "prev", p: 2, disabled: !table.getCanPreviousPage(), onClick: () => table.previousPage() },
            React.createElement(Icon, { src: "ChevronLeft" })),
        pageNumbers,
        React.createElement(PageLink, { "data-testid": "nextPage", key: "next", p: 2, disabled: !table.getCanNextPage(), onClick: () => table.nextPage() },
            React.createElement(Icon, { src: "ChevronRight" })),
        React.createElement(PageLink, { "data-testid": "lastPage", key: "lastPage", p: 2, disabled: !table.getCanNextPage(), onClick: () => table.lastPage() },
            React.createElement(Icon, { src: "ChevronDoubleRight" }))));
};
